/* 
    Add thunks here. Thunks are actions creators that can call dispatch multiple times and is in the following signature:
    
    export const someThunk = (someArg: string) => (dispatch: SomeThunkDispatchType, getState?: GetStateFunction) => Promise<void> | void;
*/
import { RootState } from 'gf-types';
import moment from 'moment';
import { ActionCreators } from 'redux-undo';
import { GoFormzServices } from '../../AppSettings';
import { getSasToken } from '../../actions';
import { getSas, loadDataUrlAsFile, uploadFromBrowser } from '../../helpers/FileUtility';
import { getDownloadUrl } from '../../helpers/FilesHelper';
import { loadImage } from '../../helpers/ImageUtility';
import { showConfirm, showBanner } from '../../helpers/MessageUtility';
import { canIUse, getSetting } from '../../helpers/PermissionUtility';
import { Features, Settings } from '../../helpers/PermissionUtility-types';
import { apimCall } from '../../helpers/api';
import { BasicElementTypes, TemplateFileTypes } from '../../helpers/constants';
import { SavingTemplateError, PublishingTemplateError } from '../../helpers/jargon';
import { getQuerystringParameters, getGuidsFromServer, getGuid } from '../../helpers/utility';
import { Template, TemplatePage } from '../../types/template-types';
import { UpdateClipboard } from '../ClipBoardManager';
import { generateClipboardFromSelectedState } from '../copyAndPaste';
import { getRecoveredTemplate, clearRecoveredTemplate } from '../recovery';
import { getTemplateState, getAllFieldPositionsWithDefaultState } from '../selectors';
import { TemplateEditorActionDispatcher, TemplateFile } from '../templateeditor-types';
import {
  TEMPLATE_UPDATE_DEFAULT_FIELD_POSITIONS,
  TEMPLATE_UPDATE_FIELD_POSITIONS_USE_DEFAULTS,
  TEMPLATE_COPY_SELECTED_ITEMS,
  TEMPLATE_PASTE_SELECTED_ITEMS,
  TEMPLATE_REPLICATE_SELECTED_ITEMS,
  TEMPLATE_EDITOR_INIT,
  TEMPLATE_RECOVER,
  TEMPLATE_EDITOR_INIT_FILES,
  TEMPLATE_RESET_BASIC_ELEMENT_UPLOADS,
  ITEMCOLLECTION_SAVE_SUCCESS,
  TEMPLATE_SAVE_FAILURE,
  TEMPLATE_PUBLISH_FAILURE,
  TEMPLATE_GET_DATASOURCES_GET_REQUEST,
  TEMPLATE_GET_DATASOURCES_GET_SUCCESS,
  TEMPLATE_GET_DATASOURCES_GET_FAILURE,
  TEMPLATE_GET_ITEMCOLLECTIONS_GET_REQUEST,
  TEMPLATE_GET_ITEMCOLLECTIONS_GET_SUCCESS,
  TEMPLATE_GET_ITEMCOLLECTIONS_GET_FAILURE,
  TEMPLATE_UNDO,
  TEMPLATE_REDO
} from './actionTypes';
import {
  getTemplateRequest,
  getTemplateSuccess,
  getTemplateFailure,
  saveTemplateRequest,
  saveTemplateSuccess,
  publishTemplateRequest,
  publishTemplateSuccess,
  templateEditorUpdateThumbnailFileId
} from './actions';
import { NavigateFunction } from 'react-router-dom';
import { createPageElementThumbnail } from '../utility';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { TEMPLATE_EDITOR_AI_SAVED_WITH_AI_FIELDS_SUCCESS } from '../ai/actions';

export const updateFieldPositionsWithDefaultState = () => (dispatch: TemplateEditorActionDispatcher, getState: () => RootState) => {
  const state = getTemplateState(getState());

  const fieldPositionsWithDefaultState = getAllFieldPositionsWithDefaultState(state);

  if (fieldPositionsWithDefaultState.length > 0) {
    dispatch({ type: TEMPLATE_UPDATE_DEFAULT_FIELD_POSITIONS, undoable: true, payload: { fieldPositionsWithDefaultState: fieldPositionsWithDefaultState } });
  }
};

export const updateSelectedFieldPositionToUseDefaultState = () => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch({ type: TEMPLATE_UPDATE_FIELD_POSITIONS_USE_DEFAULTS, undoable: true });
};

export const copySelectedItems = () => (dispatch: TemplateEditorActionDispatcher, getState: () => RootState) => {
  const state = getTemplateState(getState());
  const clipboard = generateClipboardFromSelectedState(state);
  localStorage.setItem('templateEditor_clipboard', JSON.stringify(clipboard));
  UpdateClipboard();

  dispatch({
    type: TEMPLATE_COPY_SELECTED_ITEMS,
    undoable: false,
    payload: {}
  });
  showBanner('Copied to clipboard');
};

export const duplicateSelectedItems = () => (dispatch: TemplateEditorActionDispatcher) => {
  const clipboard = localStorage.getItem('templateEditor_clipboard');

  if (clipboard) {
    dispatch({
      type: TEMPLATE_PASTE_SELECTED_ITEMS,
      undoable: true,
      payload: JSON.parse(clipboard)
    });
  }
};

export const replicateSelectedItems = () => (dispatch: TemplateEditorActionDispatcher) => {
  const clipboard = localStorage.getItem('templateEditor_clipboard');

  if (clipboard) {
    dispatch({
      type: TEMPLATE_REPLICATE_SELECTED_ITEMS,
      undoable: true,
      payload: JSON.parse(clipboard)
    });
  }
};

export const getTemplate = (templateId: string, versionId: string) => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch(getTemplateRequest());

  const url = versionId ? `/templates/${templateId}/versions/${versionId}` : `/templates/${templateId}/draft`;

  return apimCall(GoFormzServices.TemplateService, url)
    .then((response) => {
      //the following is for safely migrating old templates to basic element templates
      //if the background is not set then we know this is the first load since the basic elements release
      //so we are setting the backgrounds to the originals (no element layers)
      response?.data?.template?.pages?.forEach((page) => {
        page.backgroundPdfFileId ??= page.originalPdfFileId;
        page.backgroundFileId ??= page.fileId;
      });
      return response;
    })
    .then(async (response) => {
      await initTemplateFiles(response.data.template)(dispatch);
      return response;
    })
    .then((response) => dispatch(getTemplateSuccess(response.data.template, null, versionId)))
    .catch((error) => {
      console.error(error);
      dispatch(getTemplateFailure(error));
    });
};

export const initTemplateEditor =
  (id: string, hasRecoveredTemplate: boolean, isNewTemplate: boolean, selectedView: string) => async (dispatch: TemplateEditorActionDispatcher) => {
    dispatch({
      type: TEMPLATE_EDITOR_INIT
    });

    getSasToken()(dispatch);
    const queryParams = getQuerystringParameters(true);

    if (isNewTemplate) {
      await getGuidsFromServer(10000);
      const template: Template = {
        id: getGuid(),
        name: 'Untitled Template',
        pages: [],
        dataLayouts: [],
        fields: [],
        folderId: queryParams.folderid ? queryParams.folderid : null,
        fileId: null,
        isNew: true
      };
      let addingFormView = false;
      if (selectedView === 'list') {
        template.dataLayouts.push({
          id: getGuid(),
          dataLayoutTabs: [
            {
              id: getGuid(),
              name: 'Tab 1',
              sortNumber: 0,
              sections: [
                {
                  id: getGuid(),
                  sortNumber: 0
                }
              ]
            }
          ]
        });
      } else {
        addingFormView = true;
      }
      dispatch(getTemplateSuccess(template, addingFormView));
    } else {
      getGuidsFromServer(10000);
      await getTemplate(id, queryParams.versionid)(dispatch);
    }

    if (hasRecoveredTemplate) {
      const recoveredTemplate = getRecoveredTemplate(id);

      showConfirm(
        {
          title: 'Do you want to recover your template?',
          // eslint-disable-next-line max-len
          msg: `Do you want to restore the changes made by ${recoveredTemplate.user.name} ${moment(recoveredTemplate.timeStamp).fromNow()}? 
 
        <b>Note</b>: <i>By choosing to restore this template, you may override changes saved by other users after the above user's session ended.</i>`
        },
        async (result) => {
          if (result) {
            console.log(recoveredTemplate.template);

            dispatch({
              type: TEMPLATE_RECOVER,
              payload: {
                template: recoveredTemplate.template
              }
            });
          }

          // cleanup the recovered template
          clearRecoveredTemplate(id);
        }
      );
    }

    dispatch(ActionCreators.clearHistory());
  };

export const initTemplateFiles = (template: Template) => async (dispatch: TemplateEditorActionDispatcher) => {
  //get all the file ids from the elements on each page
  const filesFromElements =
    template?.pages
      ?.flatMap((page) => page.elements || [])
      .map((element) => {
        return { fileId: element.fileId, fileName: element.fileName, type: element.type };
      })
      .filter((element) => element.fileId)
      .filter((value, index, array) => array.findIndex((arrayValue) => value.fileId === arrayValue.fileId) === index) || [];

  const sas = await getSas();
  const downloadFilesPromises = filesFromElements.map(async (file) => {
    const url = getDownloadUrl(file.fileId, sas);
    const downloadedFile = await fetch(url).then((file) => file.blob());
    const fileDataUrl = await new Promise<string>((callback) => {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result as string);
      };
      reader.readAsDataURL(downloadedFile);
    });
    const templateFile: TemplateFile = {
      id: file.fileId,
      src: fileDataUrl,
      name: file.fileName,
      type: file.type === BasicElementTypes.Image ? TemplateFileTypes.Image : null
    };
    return templateFile;
  });

  dispatch({
    type: TEMPLATE_EDITOR_INIT_FILES,
    payload: {
      files: await Promise.all(downloadFilesPromises)
    }
  });
};

export const saveBasicElementUploads = async (dispatch: TemplateEditorActionDispatcher, getState: () => RootState): Promise<TypeMePlease> => {
  const sas = await getSas();
  const state = getTemplateState(getState());
  const elementUploads = state.elementUploads || [];
  const templatePages = state.template?.pages || [];

  //upload all the images to blobs
  const fileUploadPromises = elementUploads
    .filter((elementUpload) => elementUpload.fileId && elementUpload.fileSrc)
    .map(async (elementUpload) => {
      const file = loadDataUrlAsFile(elementUpload.fileSrc);
      const imgElement = await loadImage(file);
      return {
        metadata: { width: imgElement.width, height: imgElement.height, contentType: file.type },
        uploadResult: await uploadFromBrowser(file, elementUpload.fileId)
      };
    });
  const fileUploadResults = await Promise.all(fileUploadPromises);

  const files = fileUploadResults.map((file) => {
    return {
      id: file.uploadResult.id,
      url: file.uploadResult.url,
      width: file.metadata.width,
      height: file.metadata.height,
      contentType: file.metadata.contentType
    };
  });

  const result = await apimCall(GoFormzServices.FileService, '/files/metadata', 'POST', files);
  if (result.status !== 200) {
    throw result;
  }

  const initialTemplate = state.initialTemplateJSON == null ? null : JSON.parse(state.initialTemplateJSON);
  const pdfLayerUploadPromises = elementUploads
    .filter((elementUpload) => elementUpload.pageId && elementUpload.getCanvasSvgs)
    .map(async (elementUpload) => {
      //find the matching template page
      const page = templatePages.find((templatePage) => templatePage.id === elementUpload.pageId);
      if (page == null) {
        return null;
      }

      const originalPage = (initialTemplate?.pages || []).find((initialPage) => initialPage.id === page.id);

      //check to see if the elements have changed. we only want to upload to the server if they have.  this keeps us from savin erroneous amounts of files
      const ignoreNulls = (_key, value) => {
        if (value !== null) return value;
      };

      const elementsHaveNotChanged =
        originalPage != null &&
        originalPage.backgroundFileId === page.backgroundFileId &&
        ((!originalPage.elements?.length && !page.elements?.length) ||
          JSON.stringify(originalPage.elements, ignoreNulls) === JSON.stringify(page.elements, ignoreNulls));
      if (elementsHaveNotChanged) {
        return null;
      }

      const canvasSvgs = await elementUpload.getCanvasSvgs();
      //double check that the

      //flatten the pdf on the server
      const result = await apimCall<{ width: number; height: number; imageFileId: string; pdfFileId: string }>(
        GoFormzServices.FileService,
        `/files/pdflayers`,
        'POST',
        {
          pdfBlobPath: `${sas.fileStoragePrefix}/${page.backgroundPdfFileId || page.originalPdfFileId}`,
          layers: canvasSvgs.map((canvasSvg) => {
            return {
              svg: canvasSvg.svg,
              fontName: canvasSvg.fontFamily
              //font: byte array for custom font support.
            };
          })
        }
      );
      if (result.status !== 200) {
        throw result;
      }

      //technically the width\height can be adjusted by the layering but we want to ensure consistency with the original background
      //so keep the spec as the original width/height
      //width: result.data.width,
      //height: result.data.height,

      //add the resulting file ids to the page array so we can apply it to the template state
      return {
        id: elementUpload.pageId,
        fileId: result.data.imageFileId,
        originalPdfFileId: result.data.pdfFileId
      };
    });

  const pages: TemplatePage[] = await Promise.all(pdfLayerUploadPromises);
  dispatch({
    type: TEMPLATE_RESET_BASIC_ELEMENT_UPLOADS,
    payload: {
      template: {
        pages: pages.filter((page) => page)
      }
    }
  });
};

export const saveItemCollections = (dispatch: TemplateEditorActionDispatcher, getState: () => RootState): Promise<TypeMePlease> => {
  const state = getTemplateState(getState());
  const itemCollections = state.itemCollections;
  const dirtyItemCollections = itemCollections ? itemCollections.filter((x) => x.isDirty) : null;

  if (!dirtyItemCollections) {
    return Promise.resolve();
  }

  for (const itemCollection of dirtyItemCollections) {
    const itemCollectionId = itemCollection.id;
    const itemCollectionJson = itemCollection;

    return apimCall(GoFormzServices.TemplateService, `/itemcollections/${itemCollectionId}`, 'PUT', itemCollectionJson).then(() =>
      dispatch({
        type: ITEMCOLLECTION_SAVE_SUCCESS,
        payload: {
          itemCollections: dirtyItemCollections
        }
      })
    );
  }
};

export const updatePageElementPreviewThumbnails = async (dispatch: TemplateEditorActionDispatcher, getState: () => RootState): Promise<TypeMePlease> => {
  // GD-22278
  // since pages with elements on them could change, we need to update the thumbnail.
  // currently, only pages that are added or modified from the page editor pain will generate a new
  // thumbnail. we need to make sure that page element updates also trigger a new thumbnail.
  const state = getState();
  const { template } = getTemplateState(state);
  const sasToken = state.shared.sasToken;
  const { pages } = template;
  const thumbnailFileId = await createPageElementThumbnail(sasToken, pages);

  dispatch(templateEditorUpdateThumbnailFileId(thumbnailFileId));
};

export const saveTemplate = (data: unknown, navigate: NavigateFunction) => async (dispatch: TemplateEditorActionDispatcher, getState: () => RootState) => {
  dispatch(saveTemplateRequest());

  try {
    await saveBasicElementUploads(dispatch, getState);
    await saveItemCollections(dispatch, getState);
    await updatePageElementPreviewThumbnails(dispatch, getState);

    const state = getTemplateState(getState());
    const template = state.template;
    const { id: templateId, name: templateName, fileExtension, isNew } = template;
    const response = await apimCall(GoFormzServices.TemplateService, `/templates/${templateId}`, 'PUT', data ?? template);

    if (isNew && fileExtension) {
      analyticsTrack('Template Created', {
        action: 'Created',
        templateId,
        templateName,
        fileExtension
      });
    }

    dispatch(saveTemplateSuccess(response));
    // for ai metrics, we want to increment the save count
    dispatch({ type: TEMPLATE_EDITOR_AI_SAVED_WITH_AI_FIELDS_SUCCESS });

    clearRecoveredTemplate(templateId);
    showBanner('Template saved');
    navigate(`/templates/${templateId}`);
    return getTemplateState(getState())?.template;
  } catch (error) {
    const errorMessage = error?.response?.status === 400 && error?.response?.data ? error.response.data : SavingTemplateError;

    dispatch({
      type: TEMPLATE_SAVE_FAILURE,
      payload: {
        isSaving: false,
        error: {
          message: errorMessage
        },
        isFetching: false
      }
    });

    return null;
  }
};

export const publishTemplate = (revertingTo: string) => async (dispatch: TemplateEditorActionDispatcher, getState) => {
  const state = getTemplateState(getState());
  const templateId = state.template.id;

  dispatch(publishTemplateRequest());

  try {
    const response = await apimCall(
      GoFormzServices.TemplateService,
      `/templates/${templateId}/versions${revertingTo ? `?revertingTo=${revertingTo}` : ''}`,
      'POST',
      null
    );
    dispatch(publishTemplateSuccess(response));

    if (getSetting(Settings.SkipPublishedTemplatePrompt)) {
      showBanner('Template published');
    }
  } catch (error) {
    let errorMessage = PublishingTemplateError;
    if (error.response.status === 400 && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    }
    dispatch({
      type: TEMPLATE_PUBLISH_FAILURE,
      payload: {
        isPublishing: false,
        error: {
          message: errorMessage
        },
        isFetching: false
      }
    });
  }
};

export const getDataSources = () => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch({
    type: TEMPLATE_GET_DATASOURCES_GET_REQUEST
  });

  return apimCall(GoFormzServices.DataSourceService, `/datasources/search?limit=1000&includeExternalDataSources=${canIUse(Features.AccountUserList)}`)
    .then((response) =>
      dispatch({
        type: TEMPLATE_GET_DATASOURCES_GET_SUCCESS,
        payload: {
          dataSources: response.data.items,
          dataSourcesLoaded: true
        }
      })
    )
    .catch((error) =>
      dispatch({
        type: TEMPLATE_GET_DATASOURCES_GET_FAILURE,
        payload: {
          error: { message: error }
        }
      })
    );
};

export const getItemCollections = () => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch({
    type: TEMPLATE_GET_ITEMCOLLECTIONS_GET_REQUEST
  });
  return apimCall(GoFormzServices.TemplateService, '/itemcollections')
    .then((response) =>
      dispatch({
        type: TEMPLATE_GET_ITEMCOLLECTIONS_GET_SUCCESS,
        payload: {
          itemCollections: response.data,
          itemCollectionsLoaded: true
        }
      })
    )
    .catch((error) =>
      dispatch({
        type: TEMPLATE_GET_ITEMCOLLECTIONS_GET_FAILURE,
        payload: {
          error: { message: error }
        }
      })
    );
};

export const templateEditorUndo = () => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch(ActionCreators.undo());
  dispatch({
    type: TEMPLATE_UNDO,
    payload: {
      isDirty: true
    }
  });
};

export const templateEditorRedo = () => (dispatch: TemplateEditorActionDispatcher) => {
  dispatch(ActionCreators.redo());
  dispatch({
    type: TEMPLATE_REDO,
    payload: {
      isDirty: true
    }
  });
};
